import { useCallback, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router'

import { Footer } from '@writefull/ui'
import { clsx } from '@writefull/utils'

import useSessionStore from '@infrastructure/stores/session/session-store'

import Container from '@ui/shared/components/Container'
import PapersBanner from '@ui/shared/components/PapersBanner'
import { PAPERS_BANNER } from '@ui/shared/components/papersBannerCopy'

import { NonVisiblePage, Page, isVisiblePage } from './domain/page'

import Header from './Header'
import Hero from './Hero'
import Menu from './Menu'
import ShowSupportForInstitution from './ShowSupportForInstitution'
import useHasScroll from './hooks/useHasScroll'

interface LayoutProps {
  pages: (Page | NonVisiblePage)[]
}

export default function Layout({ pages }: LayoutProps) {
  const location = useLocation()
  const intervalId = useRef<NodeJS.Timeout | null>(null)
  const overflowContainerRef = useRef<HTMLDivElement>(null)
  const page = pages.find((page) => page.path === location.pathname) ?? pages[0]
  const hasScrolled = useHasScroll({ overflowContainerRef })

  const startSignIn = useSessionStore.use.start()
  const finishSignIn = useSessionStore.use.finish()

  const cleanUpInterval = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current)
      intervalId.current = null
    }
  }, [])

  const signIn = useCallback(() => {
    cleanUpInterval()
    startSignIn()
    const targetOrigin = import.meta.env.VITE_WF_CUSTOMER_PORTAL_URL
    window.open(`${targetOrigin}/login?callback=x`, '_blank', '')

    const getCookieValue = (name: string) => {
      const cookieValue = document.cookie
        .split('; ')
        .find((cookie) => cookie.startsWith(`${name}=`))
        ?.split('=')[1]

      if (cookieValue) {
        const decodedValue = atob(cookieValue)
        return JSON.parse(decodedValue).value
      }

      return null
    }

    const checkCookie = () => {
      const sessionCookieValue = getCookieValue('writefull_session')
      const idTokenCookieValue = getCookieValue('writefull_session_id_token')
      const configCookieValue = getCookieValue('writefull_session_config')
      if (sessionCookieValue && idTokenCookieValue && configCookieValue) {
        cleanUpInterval()

        const fullSession = {
          ...sessionCookieValue,
          idToken: idTokenCookieValue,
          user: {
            ...sessionCookieValue.user,
            config: configCookieValue,
          },
        }
        if (fullSession?.user) {
          finishSignIn(fullSession, fullSession.user)
        }
      }
    }

    intervalId.current = setInterval(checkCookie, 1_000)
  }, [cleanUpInterval, finishSignIn, startSignIn])

  useEffect(() => {
    overflowContainerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [location.pathname])

  return (
    <>
      <div
        className={clsx(
          'grid md:grid-cols-[300px_1fr] h-dvh overflow-hidden',
          'theme theme-transition',
        )}
      >
        <Menu pages={pages.filter(isVisiblePage)} />
        <div
          ref={overflowContainerRef}
          className='pai-scroll flex flex-col w-full h-dvh overflow-auto'
        >
          {/* Full-width opaque bar backing the banner; goes sticky in the `sticky`
              position arm (see index.css). `theme` matches the scroll container's
              own background so the static arm looks identical to before. */}
          <div className='pai-bar theme'>
            <div className='mx-auto w-full max-w-7xl'>
              <PapersBanner {...PAPERS_BANNER} />
            </div>
          </div>
          <ShowSupportForInstitution />
          <Header hasScrolled={hasScrolled} onSignInClick={signIn} />
          <div className='flex flex-col gap-4 md:gap-12 flex-1 pt-12'>
            <Hero {...page} />
            <main>
              <Outlet />
            </main>
            <footer>
              <Container>
                <Footer />
              </Container>
            </footer>
          </div>
        </div>
      </div>
    </>
  )
}
